import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import EPageLoader from '@/components/everridge/EPageLoader';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminRentChargeMainTab from '@/features/superAdmin/features/data/rentCharges/components/SuperAdminRentChargeMainTab';
import SuperAdminRentChargeInfosTab from '@/features/superAdmin/features/data/rentCharges/components/SuperAdminRentChargeInfosTab';
import { useSuperAdminRentChargeDetailApi } from '@/features/superAdmin/features/data/rentCharges/hooks/useSuperAdminRentChargeDetailApi';

type RentChargeDetailTab = 'main' | 'infos';

const SuperAdminRentChargeDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { rentCharge, isLoading } = useSuperAdminRentChargeDetailApi(id);
    const [activeTab, setActiveTab] = useState<RentChargeDetailTab>('main');

    if (userRole !== Roles.superAdmin) return null;

    const goBack = () => navigate('/superAdmin/data/rent-charges');

    const tabs: { id: RentChargeDetailTab; label: string }[] = [
        { id: 'main', label: t('superAdmin.data.rentCharges.tabs.main') },
        { id: 'infos', label: t('superAdmin.data.rentCharges.tabs.infos') },
    ];

    const pageTitle = rentCharge?.documentNumber
        ? `${t('superAdmin.data.rentCharges.detail.title')} — ${rentCharge.documentNumber}`
        : t('superAdmin.data.rentCharges.detail.title');

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            {isLoading && !rentCharge && <EPageLoader />}

            <EPageHeader title={pageTitle}>
                <Button variant="outline" className="gap-2" onClick={goBack}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.rentCharges.backToList')}
                </Button>
            </EPageHeader>

            {!rentCharge && !isLoading ? (
                <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6 text-sm">
                    {t('elements.table.body.empty')}
                </div>
            ) : rentCharge ? (
                <>
                    <div className="flex flex-wrap gap-2 border-b border-popover-100 pb-2">
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                variant={activeTab === tab.id ? 'default' : 'outline'}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>

                    {activeTab === 'main' && <SuperAdminRentChargeMainTab rentCharge={rentCharge} />}

                    {activeTab === 'infos' && <SuperAdminRentChargeInfosTab infos={rentCharge.infos} />}
                </>
            ) : null}
        </section>
    );
};

export default SuperAdminRentChargeDetailPage;
